import { useState } from "react";
import api, { errMsg, openBlob } from "@/lib/api";
import { toast } from "sonner";
import { Card, StateBadge, fmt, inputCls, btnPrimary, Label } from "@/components/portal/PortalUI";
import { CheckCircle2, Circle, ArrowRight, CalendarClock, User, Users, Award, Linkedin, Download } from "lucide-react";

const CertificateCard = ({ c }) => {
  const d = new Date(`${c.issue_date}T12:00:00`);
  const li = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(`AAWTA Driven Together — ${c.programs.join(" & ")}`)}&organizationName=${encodeURIComponent("African American Women in Trucking Association")}&issueYear=${d.getFullYear()}&issueMonth=${d.getMonth() + 1}&certUrl=${encodeURIComponent(c.verify_url)}&certId=${c.cert_id}`;
  return (
    <div className="bg-white border-2 border-magenta rounded-xl p-6 flex flex-wrap items-center gap-5" data-testid="certificate-card">
      <Award className="text-magenta" size={44} />
      <div className="flex-1 min-w-[220px]"><p className="text-xs uppercase tracking-widest text-magenta font-bold">Certificate of Completion</p>
        <p className="font-display text-xl font-black text-navy">{c.programs.join(" & ")}</p><p className="text-xs text-dark-text/50">Issued {fmt(c.issue_date)} · ID {c.cert_id}</p></div>
      <button onClick={() => openBlob("/student/certificate", { download: true, filename: "AAWTA Certificate.pdf" })} className={btnPrimary} data-testid="certificate-download"><Download size={16} /> Download PDF</button>
      <a href={li} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0A66C2] text-white font-bold py-2.5 px-5 rounded-md" data-testid="certificate-linkedin"><Linkedin size={16} /> Add to LinkedIn</a>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="bg-white rounded-lg border border-navy/10 p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wider text-dark-text/50 font-semibold">{label}</p>
    <p className="font-display text-2xl font-black text-navy mt-1">{value}</p>
  </div>
);

const ProfileForm = ({ s, reload }) => {
  const ob = s.onboarding || {};
  const [f, setF] = useState({ legal_name: ob.legal_name || s.name, phone: ob.phone || s.phone || "", emergency_contact_name: ob.emergency_contact_name || "", emergency_contact_phone: ob.emergency_contact_phone || "" });
  const save = async () => {
    try { await api.post("/student/onboarding/profile", f); toast.success("Profile confirmed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Confirm Your Profile" testid="onboarding-profile-form">
      <div className="grid sm:grid-cols-2 gap-3">
        {[["legal_name", "Full legal name"], ["phone", "Phone"], ["emergency_contact_name", "Emergency contact name"], ["emergency_contact_phone", "Emergency contact phone"]].map(([k, l]) => (
          <div key={k}><Label>{l}</Label><input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} data-testid={`profile-${k}`} /></div>
        ))}
      </div>
      <button onClick={save} className={`${btnPrimary} mt-4`} data-testid="profile-save">Confirm Profile</button>
    </Card>
  );
};

export const StudentOverview = ({ data, reload, goTo, welcome }) => {
  const s = data.student;
  const profileDone = !!(s.onboarding || {}).profile_confirmed_at;
  return (
    <div className="space-y-6">
      <div className="bg-navy rounded-xl text-white p-6 sm:p-8 relative overflow-hidden" data-testid="student-welcome">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-magenta/20" />
        <p className="text-magenta text-xs font-bold uppercase tracking-[0.2em]">{welcome ? "Account activated" : "Welcome back"}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2" data-testid="student-name">{s.name}</h1>
        <div className="flex flex-wrap gap-2 mt-4">
          {s.programs.map((p) => <span key={p} className="bg-white/10 text-white text-xs font-semibold px-3 py-1 rounded-full" data-testid="student-program">{p}</span>)}
          {s.cohort && <span className="bg-magenta text-white text-xs font-semibold px-3 py-1 rounded-full">Cohort: {s.cohort}</span>}
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="flex justify-between text-xs text-white/70 mb-1"><span>Overall progress</span><span data-testid="student-progress">{data.progress}%</span></div>
            <div className="h-2.5 rounded-full bg-white/15"><div className="h-2.5 rounded-full bg-magenta transition-all" style={{ width: `${data.progress}%` }} /></div>
          </div>
          <div className="bg-white text-navy rounded-lg px-4 py-3 text-sm" data-testid="student-next-action">
            <span className="text-xs text-dark-text/50 block">Next required action</span><strong>{data.next_action}</strong>
          </div>
        </div>
      </div>

      {s.certificate && <CertificateCard c={s.certificate} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Enrollment" value={s.status} testid="stat-enrollment" />
        <Stat label="Registration" value={s.registration_status} testid="stat-registration" />
        <Stat label="Documents to complete" value={data.documents.filter((d) => !d.completed_at).length} testid="stat-docs" />
        <Stat label="Quizzes assigned" value={data.quizzes.length} testid="stat-quizzes" />
      </div>

      {!profileDone && <ProfileForm s={s} reload={reload} />}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card title="Onboarding Checklist" className="lg:col-span-2" testid="onboarding-checklist">
          <ul className="divide-y divide-navy/5">
            {data.checklist.map((c) => (
              <li key={c.key} className="flex items-center gap-3 py-3" data-testid={`checklist-${c.key}`}>
                {c.done ? <CheckCircle2 className="text-emerald-600 shrink-0" size={20} /> : <Circle className="text-navy/30 shrink-0" size={20} />}
                <span className={`flex-1 text-sm ${c.done ? "text-dark-text/50 line-through" : "text-navy font-semibold"}`}>{c.label}</span>
                {c.due_date && !c.done && <span className="text-xs text-dark-text/50">Due {fmt(c.due_date)}</span>}
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Your Support Team" testid="support-team">
          <div className="space-y-4 text-sm">
            <div className="flex gap-3"><User className="text-magenta" size={18} /><div><p className="text-xs text-dark-text/50">Instructor / Trainer</p><p className="font-semibold text-navy" data-testid="student-instructor">{s.instructor_name || s.instructor_email || "To be assigned"}</p></div></div>
            <div className="flex gap-3"><Users className="text-magenta" size={18} /><div><p className="text-xs text-dark-text/50">Mentor</p><p className="font-semibold text-navy">{s.mentor_name || s.mentor_email || "To be assigned"}</p></div></div>
            <div className="text-xs text-dark-text/50">Enrolled {fmt(s.enrolled_at)}{s.program_start_date ? ` · Program starts ${fmt(s.program_start_date)}` : ""}</div>
          </div>
        </Card>
      </div>

      <Card title="Upcoming Dates & Deadlines" testid="student-deadlines">
        {data.deadlines.length === 0 ? <p className="text-sm text-dark-text/50">No upcoming deadlines.</p> : (
          <ul className="divide-y divide-navy/5">
            {data.deadlines.map((d, i) => (
              <li key={i} className="flex flex-wrap items-center gap-3 py-3">
                <CalendarClock size={18} className="text-navy/40" />
                <span className="text-xs font-bold uppercase text-dark-text/50 w-16">{d.type}</span>
                <span className="flex-1 text-sm font-semibold text-navy">{d.title}</span>
                <span className="text-xs text-dark-text/60">{fmt(d.due_date)}</span>
                <StateBadge state={d.state} />
                <button onClick={() => goTo({ Document: "documents", Upload: "uploads", Course: "courses", Quiz: "quizzes" }[d.type])} className="text-magenta"><ArrowRight size={16} /></button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};
